import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Objects } from "components/Object/Object";
import { Html, useAnimations, useGLTF } from "@react-three/drei";
import { Character } from "components/Character/Character";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

const classroomIconPaths = [
  "/assets/graphics/classroom_icons/BlueBear.png",
  "/assets/graphics/classroom_icons/Calendar.png",
  "/assets/graphics/classroom_icons/BlueFlower.png",
  "/assets/graphics/classroom_icons/Scrapbook.png",
  "/assets/graphics/classroom_icons/MusicVideo.png",
];

// Easy-to-edit screen positions for Classroom icons.
const classroomIconLayout = [
  { left: "5%", top: "3%" },
  { left: "13%", top: "3%" },
  { left: "5%", top: "17%" },
  { left: "13%", top: "17%" },
  { left: "9%", top: "30%" },
];

const classroomIconBehaviours = [
  "resource_blueBear",
  "calendar",
  "resource_blueFlower",
  "scrapbook",
  "musicVideo",
];

// Extracted so useGLTF is never called with an empty path
const BgAsset = ({ path }: { path: string }) => {
  const gltfAsset = useGLTF(path);
  const { animations } = gltfAsset;
  const { actions } = useAnimations(animations, gltfAsset.scene);
  useEffect(() => {
    if (actions && Object.keys(actions).length > 0) {
      const action = actions[Object.keys(actions)[0]];
      if (action) {
        action.play();
      }
    }
  }, [actions]);
  return <primitive object={gltfAsset.scene} />;
};

const Scene = ({
  data,
  selectedQuestion,
  setSelectedQuestion,
  sceneName,
  onSceneLoaded,
  setIsQuizMode,
  setModalContent,
  quizCompletions,
  onIntroStart,
  onIntroEnd,
  skipIntroRef,
  replayIntroRef,
  onCameraLerpChange,
  isCameraLerping,
}: any) => {
  const gltf = useGLTF(data.background as string);

  useEffect(() => {
    if (sceneName === "Classroom") {
      if (!Array.isArray(gltf)) {
        gltf.scene.children.map((child: any) => {
          const chalkboardDrawings = [
            "ChalkboardDrawings",
            "ChalkboardDrawings001",
            "ChalkboardDrawings002",
            "ChalkboardDrawings003",
            "ChalkboardDrawings004",
          ];
          if (chalkboardDrawings.includes(child.name)) {
            child.visible = false;
          }

          if (child.name === "Left_Wall002") {
            console.log(child);
          }
        });
      }
    }

    if (sceneName === "HearingRoom") {
      if (!Array.isArray(gltf)) {
        gltf.scene.children.map((child: any) => {
          if (child.isLight) {
            if (child.isDirectionalLight) child.intensity = 1.5;
          }
        });
      }
    }

    if (sceneName === "LakeBearbine") {
      if (!Array.isArray(gltf)) {
        gltf.scene.children.map((child: any) => {
          if (child.name === "Sun") {
            child.intensity = 1.9; // Reduced from 3 to avoid fur texture blowout
          }
        });
      }
    }

    if (sceneName === "ParliamentHill") {
      if (!Array.isArray(gltf)) {
        gltf.scene.children.map((child: any) => {
          if (child.name === "Point001") {
            child.intensity = 2;
            child.decay = 1;
          }
          if (child.name === "Point003") {
            child.intensity = 3;
            child.decay = 1;
          }
          if (child.name === "Sun") child.intensity = 0.5;
          if (child.name === "Sun001") child.intensity = 1;
        });
      }
    }

    if (sceneName === "Classroom") {
      if (!Array.isArray(gltf)) {
        gltf.scene.children.map((child: any) => {
          if (child.name === "roomLight") child.intensity = 3.33; // originally: 50
          if (child.name === "Spot_1") child.intensity = 13.33; // originally: 200
          if (child.name === "Spot001") child.intensity = 10; // originally: 150
          if (child.name === "roomLight001") child.intensity = 33.33; // originally: 500
          if (child.name === "roomLight002") child.intensity = 33.33; // originally: 500
          if (child.name === "roomLight003") child.intensity = 6.66; // originally: 100
          if (child.name === "roomLight004") child.intensity = 33.33; // originally: 500
          if (child.name === "Spot002_1") child.intensity = 6.66; // originally: 100
          if (child.name === "Spot004_1") child.intensity = 6.66; // originally: 100
          if (child.name === "Spot005") child.intensity = 6.66; // originally: 100
          if (child.name === "Spot006") child.intensity = 6.66; // originally: 100
          if (child.name === "Spot011") child.intensity = 0; // originally: 50
          if (child.name === "Spot012") child.intensity = 0; // originally: 50
          if (child.name === "Spot013_1") child.intensity = 3.33; // originally: 50
          if (child.name === "Spot014") child.intensity = 0; // originally: 100
          if (child.name === "Spot010") child.intensity = 0; // originally: 100
        });
      }
    }

    if (onSceneLoaded) {
      onSceneLoaded();
    }
  }, [gltf]);

  // Camera transitions
  const { camera } = useThree();

  // Extract cameras synchronously so the initial position is set before first paint
  const { camera1, camera2, camera3 } = useMemo(() => {
    if (Array.isArray(gltf))
      return { camera1: null, camera2: null, camera3: null };
    let camera1: any = null,
      camera2: any = null,
      camera3: any = null;
    gltf.scene.children.forEach((child: any) => {
      if (child.isCamera) {
        if (child.name === "Camera001") camera1 = child;
        else if (child.name === "Camera002") camera2 = child;
        else if (child.name === "Camera003") camera3 = child;
      }
    });
    return { camera1, camera2, camera3 };
  }, [gltf]);
  const transitionProgress = useRef(0);
  // useRef instead of useState so useFrame always reads the latest value synchronously,
  // avoiding a one-frame camera snap when transitionProgress is reset at the same time.
  const transitionRef = useRef<"mount" | "question" | "empty">("mount");
  const introReadySetRef = useRef(false);
  const [introReady, setIntroReady] = useState(false);
  const [isClassroomCameraLerping, setIsClassroomCameraLerping] =
    useState(false);
  const [isClassroomIntroPlaying, setIsClassroomIntroPlaying] = useState(false);

  useEffect(() => {
    if (sceneName === "Classroom") {
      setIsClassroomCameraLerping(true);
      setIsClassroomIntroPlaying(false);
    } else {
      setIsClassroomCameraLerping(false);
      setIsClassroomIntroPlaying(false);
    }
  }, [sceneName]);

  // Set the initial camera position and quaternion before first paint
  useLayoutEffect(() => {
    if (camera1) {
      camera.position.set(
        camera1.position.x,
        camera1.position.y,
        camera1.position.z,
      );
      camera.quaternion.set(
        camera1.quaternion.x,
        camera1.quaternion.y,
        camera1.quaternion.z,
        camera1.quaternion.w,
      );

      if (sceneName === "ParliamentHill") {
        (camera as THREE.PerspectiveCamera).fov = 40.5; // Keith comment: 65.5, best?: 40.5
      } else if (sceneName === "Classroom") {
        (camera as THREE.PerspectiveCamera).fov = 46;
      } else {
        (camera as THREE.PerspectiveCamera).fov = 23.7;
      }
      camera.updateProjectionMatrix();
    }
  }, [camera1, camera]);

  const handleTransition = (
    startPos: THREE.Vector3,
    startQuat: THREE.Quaternion,
    endPos: THREE.Vector3,
    endQuat: THREE.Quaternion,
    lerpSpeed: number,
  ) => {
    camera.position.lerpVectors(startPos, endPos, transitionProgress.current);
    camera.quaternion.slerpQuaternions(
      startQuat,
      endQuat,
      transitionProgress.current,
    );
    transitionProgress.current += lerpSpeed;
  };

  // Check for the transition keyword and animate the camera
  useFrame((state, delta) => {
    const lerpSpeed =
      (sceneName === "ParliamentHill" && transitionRef.current === "mount"
        ? 0.3
        : 0.5) * delta; // Adjust speed

    if (
      transitionRef.current === "mount" &&
      camera1 &&
      camera2 &&
      transitionProgress.current < 1
    ) {
      if (sceneName === "Classroom" && onCameraLerpChange)
        onCameraLerpChange(true);
      if (sceneName === "Classroom") setIsClassroomCameraLerping(true);
      handleTransition(
        camera1.position,
        camera1.quaternion,
        camera2.position,
        camera2.quaternion,
        lerpSpeed,
      );
      // Snap to exact target once progress reaches 1 so distanceTo check always passes
      if (transitionProgress.current >= 1) {
        camera.position.copy(camera2.position);
        camera.quaternion.copy(camera2.quaternion);
      }
    } else if (
      transitionRef.current === "mount" &&
      camera1 &&
      camera2 &&
      !introReadySetRef.current &&
      camera.position.distanceTo(camera2.position) < 0.01
    ) {
      introReadySetRef.current = true;
      setIntroReady(true);
      if (sceneName === "Classroom") setIsClassroomCameraLerping(false);
    } else if (
      transitionRef.current === "question" &&
      camera2 &&
      camera3 &&
      transitionProgress.current < 1
    ) {
      handleTransition(
        camera2.position,
        camera2.quaternion,
        camera3.position,
        camera3.quaternion,
        lerpSpeed,
      );
    } else if (
      transitionRef.current === "empty" &&
      camera3 &&
      camera2 &&
      transitionProgress.current < 1
    ) {
      handleTransition(
        camera3.position,
        camera3.quaternion,
        camera2.position,
        camera2.quaternion,
        lerpSpeed,
      );
    }
  });

  // Set the transition keyword and transitionProgress
  useEffect(() => {
    if (selectedQuestion) {
      transitionRef.current = "question";
    } else if (transitionRef.current === "question") {
      // Only reset to 'empty' if it was in 'question' state
      transitionRef.current = "empty";
    }
    transitionProgress.current = 0;
  }, [selectedQuestion]);

  useFrame((state, delta) => {
    // Wave animation at the Lake
    if (sceneName === "LakeBearbine") {
      // @ts-ignore
      if (gltf.materials.shore_ripples.map.offset.y >= 1) {
        // @ts-ignore
        gltf.materials.shore_ripples.map.offset.y = 0;
      } else {
        // @ts-ignore
        gltf.materials.shore_ripples.map.offset.y += 0.001;
      }
    }
  });

  const handleClassroomIconClick = (index: number) => {
    if (isClassroomIntroPlaying) return; // Disable clicks during intro

    const behaviour = classroomIconBehaviours[index];

    if (behaviour === "resource_blueBear") {
      setModalContent({
        type: "resource",
        image: "assets/graphics/resources/Jordan_780x900.png",
        description_en:
          "This is Jordan River Anderson. Jordan was a special boy from Norway House Cree Nation who gave us the gift of Jordan's Principle. He loved teddy bears!",
        description_fr:
          "Voici Jordan River Anderson. Jordan était un garçon exceptionnel de la Nation crie de Norway House qui nous a offert le « Principe de Jordan ». Il adorait les ours en peluche !",
      });
      return;
    }

    if (behaviour === "resource_blueFlower") {
      setModalContent({
        type: "resource",
        image: "assets/graphics/resources/Shannen_780x900.png",
        description_en:
          "The Shannen's Dream campaign is named after Shannen Koostachin from Attawapiskat First Nation. Shannen wanted all First Nations children to be able to live their dreams.",
        description_fr:
          "La campagne du « Rêve de Shannen » porte le nom de Shannen Koostachin, de la Première Nation d'Attawapiskat. Shannen souhaitait que tous les enfants des Premières Nations puissent réaliser leurs rêves.",
      });
      return;
    }

    if (behaviour === "calendar") {
      setModalContent({ type: "calendar" });
      return;
    }

    if (behaviour === "scrapbook") {
      setModalContent({ type: "scrapbook" });
      return;
    }

    if (behaviour === "musicVideo") {
      setModalContent({ type: "musicVideo" });
      return;
    }
  };

  return (
    <>
      {data.characters.map((characterData: any, index: number) => (
        <Character
          key={characterData.file}
          index={index}
          modelPath={characterData.file}
          animPath={characterData.idleAnimation}
          characterData={characterData}
          questionData={selectedQuestion}
          setSelectedQuestion={setSelectedQuestion}
          introAnimPath={characterData.introAnimPath}
          introAudioPath={characterData.introAudioPath}
          introReady={introReady}
          onIntroStart={() => {
            if (sceneName === "Classroom") setIsClassroomIntroPlaying(true);
            onIntroStart?.();
          }}
          onIntroEnd={() => {
            if (sceneName === "Classroom") setIsClassroomIntroPlaying(false);
            onIntroEnd?.();
          }}
          skipIntroRef={skipIntroRef}
          replayIntroRef={replayIntroRef}
          onCameraLerpChange={onCameraLerpChange}
        />
      ))}

      {/* Neutral ambient fill for LakeBearbine — avoids colour tinting fur */}
      {sceneName === "LakeBearbine" && <ambientLight intensity={0.45} />}

      {/* Display background */}
      <primitive object={gltf.scene} />

      {/* Show bg asset (butterflies etc.) only when available */}
      {data.bgAsset && <BgAsset path={data.bgAsset} />}

      {sceneName === "Classroom" ? (
        !isClassroomCameraLerping && !isCameraLerping ? (
          <Html fullscreen zIndexRange={[10, 0]}>
            <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
              }}
            >
              {classroomIconLayout.map((position, index) => (
                <button
                  key={`classroom-icon-${index + 1}`}
                  aria-label={`Classroom icon ${index + 1}`}
                  onClick={() => handleClassroomIconClick(index)}
                  style={{
                    position: "absolute",
                    left: position.left,
                    top: position.top,
                    transform: "translate(-50%, -50%)",
                    opacity: isClassroomIntroPlaying ? 0.5 : 1,
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                    pointerEvents: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "999px",
                      filter:
                        "drop-shadow(0 0.26vw 0.65vw rgba(255, 255, 255, 0.65)) drop-shadow(0 0 1.05vw rgba(255, 255, 255, 0.55))",
                      WebkitFilter:
                        "drop-shadow(0 0.26vw 0.65vw rgba(255, 255, 255, 0.65)) drop-shadow(0 0 1.05vw rgba(255, 255, 255, 0.55))",
                      padding: "8px",
                    }}
                  >
                    <img
                      src={classroomIconPaths[index]}
                      alt={`Classroom icon ${index + 1}`}
                      style={{
                        width: "clamp(28px, 5.33vw, 110px)",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </Html>
        ) : null
      ) : (
        <Objects
          distanceFactor={data.htmlDistanceFactor}
          questionsData={data.questions}
          sceneName={sceneName}
          selectedQuestion={selectedQuestion}
          setSelectedQuestion={setSelectedQuestion}
          objectPath={data.object}
          objectIntroPath={(data as any).objectIntro}
          objectIntroEndTime={(data as any).objectIntroEndTime}
          quizCompletions={quizCompletions}
          setIsQuizMode={setIsQuizMode}
          setModalContent={setModalContent}
        />
      )}
    </>
  );
};

export default Scene;
